import { usePersistHorizontalScroll } from '../../hooks/usePersistHorizontalScroll';
import { useCalculateFetchedItems } from '../../hooks/useCalculateFetchItems';
import { useSyncHorizontalScroll } from '../../hooks/useSyncHorizontalScroll';
import React, { useCallback, useRef, useEffect, useState } from 'react';
import { useScrollFetchData } from '../../hooks/useScrollFetchData';
import CustomHeaderCell from '../../utils/customHeaderCell';
import MachinesListViewData from './machineListViewData';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PersistedFixedSizeList as FixedSizeList } from '../../hooks/listNavigation';
import { useSelector } from 'react-redux';
import { Box } from '@mui/material';
import { debounce } from 'lodash';

const defaultColumnWidth = {
  name: 250,
  cost_status: 250,
  region: 250,
  site: 250,
  pole: 250,
  metier: 250,
  marque: 250,
  status: 250,
  type: 250,
  internal_codification: 250,
  serial_number: 250,
  control_date: 250,
  contract_start_date: 250,
  contract_end_date: 250,
  purchase_date: 250,
  purchase_price: 250,
  commissioning_date: 250,
  manufacture_date: 250,
  electric_power: 250,
  intensity: 250,
  model: 250,
  dimensions: 250,
  entreprise: 350,
};

const machine_status = [
  { id: 'Valide', name: 'Valide' },
  { id: 'AvecReparation', name: 'Avec Reparation' },
  { id: 'NonConforme', name: 'Non Conforme' },
];

const ListView = React.memo(({ scrollRef, machineUndefinedData, filteredMachinesCount, setMachinesFetched, machines, setOffset, filters, setFilters, regions, sites, poles, metiers, brands, types, status, sortBy, setSortBy }) => {

  const { t } = useTranslation();
  const navigate = useNavigate();
  const authenticatedUser = useSelector((state) => state.AuthenticatedUser);

  const [machineColumnWidth, setMachineColumnWidth] = useState(() => {
    const storedWidths = JSON.parse(localStorage.getItem('machineColumnWidth')) || {};
    return { ...defaultColumnWidth, ...storedWidths }; // Merge defaults with stored widths
  });

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  // Update window height on resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowHeight(window.innerHeight);
    }, 300); // 300ms debounce delay
  
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel(); // Cancel any pending debounced calls
    };
  }, []);

  const tableHeadRef = useRef(null);
  const listOuterRef = useRef(null);

  // Persist horizontal scroll position
  usePersistHorizontalScroll({
    id: "machines-table",
    headRef: tableHeadRef,
    bodyRef: listOuterRef,
    // anything that tends to cause re-render/reset of scroll
    deps: [filteredMachinesCount, JSON.stringify(machineColumnWidth), filters]
  });

  // synchronize horizontal scroll between table head and list
  useSyncHorizontalScroll(tableHeadRef, scrollRef);

  // Calculate fetched items on mount
  useCalculateFetchedItems(filteredMachinesCount, setMachinesFetched, 70, 280);

  // Handle scroll and fetch more items
  const handleScrollList = useScrollFetchData(setOffset, setMachinesFetched, filteredMachinesCount, 70, 280);

  const handleMachineClick = useCallback((id) => {
    navigate(`machine_details/${id}`);
  }, [navigate]);

  const handleChangeRegion = (region) => {
    setFilters({ ...filters, selectedRegion: region, selectedSite: '', selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
  }

  const handleChangeSite = (site) => {
    setFilters({ ...filters, selectedSite: site, selectedPole: '', selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
  }

  const handleChangePole = (pole) => {
    setFilters({ ...filters, selectedPole: pole, selectedMetier: '', selectedStatus: '', selectedType: '', selectedMark: '' });
  }

  const handleChangeMetier = (metier) => {
    setFilters({ ...filters, selectedMetier: metier, selectedStatus: '', selectedType: '', selectedMark: '' });
  }

  const handleChangeStatus = (status) => {
    setFilters({ ...filters, selectedStatus: status });
  }

  const handleChangeMark = (mark) => {
    setFilters({ ...filters, selectedMark: mark });
  }

  const handleChnageType = (type) => {
    setFilters({ ...filters, selectedType: type });
  }

  const handleSort = (field) => {
    setSortBy((prevSortBy) => {
      const nextSortOrder = prevSortBy[field] === null ? 'asc'
        : prevSortBy[field] === 'asc' ? 'desc'
          : null;

      return Object.fromEntries(
        Object.keys(prevSortBy)?.map((key) => [key, key === field ? nextSortOrder : null])
      );
    });

  };

  const handleColumnWidthChange = (value, columnKey) => {
    setMachineColumnWidth({ ...machineColumnWidth, [columnKey]: value })
  }

  useEffect(() => {
    localStorage.setItem('machineColumnWidth', JSON.stringify(machineColumnWidth));
  }, [machineColumnWidth]);

  const Row = ({ index, style, data }) => {
    const { machines, regions, handleMachineClick } = data;
    const machine = index === 0 ? machines[0] : machines[index];
    return (
      <Box key={index} style={{ ...style, width: 'fit-content' }}>
        <MachinesListViewData
          machine={machine}
          regions={regions}
          columnWidth={machineColumnWidth}
          handleMachineClick={handleMachineClick}
        />
      </Box>
    );
  };

  return (
    <Box className='table'>
      <Box className='tableHead' ref={tableHeadRef}>
        <CustomHeaderCell
          label={t('machineName')}
          columnKey='name'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={true}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label='% maintenance'
          columnKey='cost_status'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={false}
          isFixed={false}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Codification interne')}
          columnKey='internal_codification'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={machineUndefinedData?.internal_codification}
          value={filters.internal_codification}
          onChange={(e) => setFilters({ ...filters, internal_codification: e.target.value })}
          options={['Indéfini']}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Modèle')}
          columnKey='model'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={machineUndefinedData?.model}
          value={filters.model}
          onChange={(e) => setFilters({ ...filters, model: e.target.value })}
          options={['Indéfini']}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Dimensions')}
          columnKey='dimensions'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={false}
          isFilter={machineUndefinedData?.width || machineUndefinedData?.height || machineUndefinedData?.length}
          value={filters.dimensions}
          onChange={(e) => setFilters({ ...filters, dimensions: e.target.value })}
          options={['Indéfini']}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Statut de contrôle')}
          columnKey='status'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.selectedStatus}
          onChange={(e) => handleChangeStatus(e.target.value)}
          options={[...machine_status.filter((ms) => status.includes(ms.id)), { id: 'Indéfini', name: 'Indéfini' }]}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Type')}
          columnKey='type'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.selectedType}
          onChange={(e) => handleChnageType(e.target.value)}
          options={types}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        {regions.length > 1 && (
          <CustomHeaderCell
            label={t('Regions')}
            columnKey='region'
            sortBy={sortBy}
            handleSort={handleSort}
            sortable={true}
            isFilter={regions.length > 1}
            value={filters.selectedRegion}
            onChange={(e) => handleChangeRegion(e.target.value)}
            options={regions}
            columnWidth={machineColumnWidth}
            onColumnWidthChange={handleColumnWidthChange}
          />
        )}
        <CustomHeaderCell
          label={t('sites')}
          columnKey='site'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={authenticatedUser.user_site_ids?.length > 1}
          value={filters.selectedSite}
          onChange={(e) => handleChangeSite(e.target.value)}
          options={sites}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('poles')}
          columnKey='pole'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={authenticatedUser.user_pole_ids?.length > 1}
          value={filters.selectedPole}
          onChange={(e) => handleChangePole(e.target.value)}
          options={poles}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Métier')}
          columnKey='metier'
          sortable={false}
          isFilter={authenticatedUser.user_metier_ids?.length > 1}
          value={filters.selectedMetier}
          onChange={(e) => handleChangeMetier(e.target.value)}
          options={metiers}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Marque')}
          columnKey='marque'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.selectedMark}
          onChange={(e) => handleChangeMark(e.target.value)}
          options={brands}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Date contrôle')}
          columnKey='control_date'
          value={filters.control_date}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Date de mise en service')}
          columnKey='commissioning_date'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.commissioning_date}
          onChange={(e) => { setFilters({ ...filters, commissioning_date: e }) }}
          options='date'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={`Entreprise d'entretien`}
          columnKey='entreprise'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Date de début du contrat')}
          columnKey='contract_start_date'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Date de fin du contrat')}
          columnKey='contract_end_date'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Puissance électrique')}
          columnKey='electric_power'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.electric_power}
          onChange={(e) => setFilters({ ...filters, electric_power: e })}
          options='number'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Intensité électrique')}
          columnKey='intensity'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.intensity}
          onChange={(e) => setFilters({ ...filters, intensity: e })}
          options='number'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={`Date d'achat`}
          columnKey='purchase_date'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.purchase_date}
          onChange={(e) => setFilters({ ...filters, purchase_date: e })}
          options='date'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={t('Date de fabrication')}
          columnKey='manufacture_date'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.manufacture_date}
          onChange={(e) => setFilters({ ...filters, manufacture_date: e })}
          options='date'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={`Prix d'achat`}
          columnKey='purchase_price'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={true}
          value={filters.purchase_price}
          onChange={(e) => setFilters({ ...filters, purchase_price: e })}
          options='number'
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell
          label={`Numéro de série`}
          columnKey='serial_number'
          sortBy={sortBy}
          handleSort={handleSort}
          sortable={true}
          isFilter={machineUndefinedData?.serial_number}
          value={filters.serial_number}
          onChange={(e) => setFilters({ ...filters, serial_number: e.target.value })}
          options={['Indéfini']}
          columnWidth={machineColumnWidth}
          onColumnWidthChange={handleColumnWidthChange}
        />
        <CustomHeaderCell width="50px" />
      </Box>

      <Box className='tableBody'>
        <FixedSizeList
          ref={scrollRef}
          outerRef={listOuterRef}
          height={Math.min(
            filteredMachinesCount * 70 + 15,
            windowHeight - 285
          )}
          width='100%'
          itemCount={filteredMachinesCount}
          itemSize={70}
          itemData={{ machines, regions, handleMachineClick }}
          onScroll={handleScrollList}
        >
          {Row}
        </FixedSizeList>
      </Box>
    </Box>

  );
});

export default ListView;

